// Accordion/modal-row panel summarizing the live PHQ-8 model: general info (name,
// version, horizon/lookback), performance (MAE/R² with traffic-light thresholds from
// MetricExplanation.tsx), and the SHAP-derived Einflussfaktoren card. Rendered inside
// PredictionAnalysisPanel/ModelInfoPanel's parent view alongside PredictionScore.tsx.
import { useState, type ReactNode } from "react"
import {
    AnalysisServiceGetPHQ8LocalShapResponsePayload,
    PHQ8ModelMetadata
} from "pokal_api"
import {
    MetricExplanationKey,
    GroupedValue,
    MetricExplanationModal,
    getActiveTrafficLight,
} from "./MetricExplanation"
import EinflussfaktorenCard from "../shap/EinflussfaktorenCard"

type ModelInfoPanelProps = {
    metadata: PHQ8ModelMetadata | null
    localShap?: AnalysisServiceGetPHQ8LocalShapResponsePayload | null
}

type SectionKey = "einflussfaktoren"

function formatNumber(value: number): string {
    if (!Number.isFinite(value)) return "-"
    return value.toFixed(4)
}

type ModalRowProps = {
    title: string
    badge?: ReactNode
    onClick: () => void
}

function ModalRow({ title, badge, onClick }: ModalRowProps) {
    return (
        <div className={"border-b border-gray-100 last:border-0"}>
            <button
                type="button"
                onClick={onClick}
                className={
                    "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                }
            >
                <span className={"flex-1 text-sm font-medium text-gray-800"}>{title}</span>
                {badge}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={"h-4 w-4 flex-shrink-0 -rotate-90 text-gray-400"}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                </svg>
            </button>
        </div>
    )
}

type AccordionRowProps = {
    title: string
    badge?: ReactNode
    isExpanded: boolean
    onToggle: () => void
    children: ReactNode
}

function AccordionRow({ title, badge, isExpanded, onToggle, children }: AccordionRowProps) {
    return (
        <div className={"border-b border-gray-100 last:border-0"}>
            <button
                type="button"
                onClick={onToggle}
                className={
                    "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                }
            >
                <span className={"flex-1 text-sm font-medium text-gray-800"}>{title}</span>
                {badge}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                </svg>
            </button>
            {isExpanded && <div className={"px-4 pb-4"}>{children}</div>}
        </div>
    )
}

export default function ModelInfoPanel({ metadata, localShap }: ModelInfoPanelProps) {
    const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
        einflussfaktoren: false
    })
    const [openModal, setOpenModal] = useState<MetricExplanationKey | null>(null)

    function toggle(key: SectionKey): void {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    if (!metadata) {
        return (
            <div className={"rounded-lg bg-gray-100 p-4 text-center text-gray-600"}>
                Keine Modellparameter verfügbar
            </div>
        )
    }

    const modelInfoValues: GroupedValue[] = [
        { label: "Modellname", value: metadata.model_name },
        { label: "Modellversion", value: metadata.model_version },
        { label: "# Trainingsdaten", value: metadata.params.n_train_rows },
        { label: "Horizont", value: `${metadata.best_horizon} Tage` },
        { label: "Lookback", value: `${metadata.best_lookback} Tage` }
    ]

    const performanceValues: GroupedValue[] = [
        {
            label: "Mean Absolute Error",
            value: formatNumber(Number(metadata.mae)),
            numericValue: Number(metadata.mae),
            kind: "mae"
        },
        {
            label: "R² Score",
            value: formatNumber(Number(metadata.r2_score)),
            numericValue: Number(metadata.r2_score),
            kind: "r2"
        }
    ]

    const performanceBand = getActiveTrafficLight(performanceValues, "model_performance")
    const modalValues = openModal === "model_info" ? modelInfoValues : performanceValues

    return (
        <div className={"rounded-lg bg-white shadow"}>

            {/* Modellinformationen */}
            <ModalRow
                title="Generelle Informationen"
                badge={
                    <span className={"flex items-center gap-1.5 text-xs text-gray-500"}>
                        <span>
                            Sagt {metadata.best_horizon} {metadata.best_horizon === 1 ? "Tag" : "Tage"} voraus
                            {" · "}
                            Basierend auf Daten {metadata.best_lookback === 1 ? "des letzten Tages" : `der letzten ${metadata.best_lookback} Tage`}
                            {" · "}
                        </span>
                        <span className={"rounded bg-gray-100 px-2 py-0.5 text-gray-600"}>
                            v{metadata.model_version}
                        </span>
                    </span>
                }
                onClick={() => setOpenModal("model_info")}
            />

            {/* Modellperformance */}
            <ModalRow
                title="Performance"
                badge={
                    performanceBand ? (
                        <span className={"flex items-center gap-1.5 text-xs"}>
                            <span
                                className={`h-2.5 w-2.5 flex-shrink-0 rounded-full border ${performanceBand.circleClassName}`}
                            />
                            <span className={performanceBand.textClassName ?? "text-gray-600"}>
                                {performanceBand.label}
                            </span>
                        </span>
                    ) : undefined
                }
                onClick={() => setOpenModal("model_performance")}
            />

            {/* Einflussfaktoren (kombiniert: Allgemein + Patient) */}
            <AccordionRow
                title="Einflussfaktoren auf die Vorhersage"
                isExpanded={expanded.einflussfaktoren}
                onToggle={() => toggle("einflussfaktoren")}
            >
                <EinflussfaktorenCard localPayload={localShap} />
            </AccordionRow>

            {openModal && (
                <MetricExplanationModal
                    kind={openModal}
                    values={modalValues}
                    onClose={() => setOpenModal(null)}
                />
            )}
        </div>
    )
}
